import * as React from "react";
// probably should give this a better name
// maybe MultiChartViewTemplate
import { useState, useMemo, useEffect } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";
import { BarChart as BarIcon, LineChart as LineIcon } from "lucide-react";
import FullscreenWrapper from "../common/FullscreenWrapper";
import { Alert, AlertDescription } from "../../components/ui/alert";
import BreakdownChart from "./BreakdownChart";
import MetricComparisonCards from "./MetricComparisonCards";
import { ExportButton, exportToExcel } from "../common/ExportToExcel";

const MultiMetricBarChart = ({ data, metrics, colors }) => {
  const handleScroll = (e) => {
    const yAxis = document.querySelector(
      ".recharts-cartesian-axis.recharts-yAxis"
    );
    if (yAxis) {
      yAxis.style.transform = `translate(${e.target.scrollLeft}px, 0)`;
    }
  };

  return (
    <div className="h-[600px] w-full">
      <style>
        {`
          .recharts-cartesian-axis.recharts-yAxis {
            transform: translate(0, 0);
            transition: transform 0s;
          }
        `}
      </style>
      <div
        className="h-full w-full overflow-x-auto overflow-y-hidden"
        onScroll={handleScroll}
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "auto",
          scrollbarColor: "rgba(0,0,0,.3) transparent",
        }}
      >
        <div
          style={{
            minWidth: Math.max(data.length * 50, 1200) + "px",
            height: "600px",
            position: "relative",
          }}
        >
          <ResponsiveContainer width="100%" height={600}>
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, bottom: 60, left: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              {metrics.map((metric, index) => (
                <Bar
                  key={metric}
                  dataKey={metric}
                  fill={colors[index]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
              <Brush dataKey="name" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const DetailedLineChart = ({ data, metrics, colors, lineStyles = [] }) => {
  const handleScroll = (e) => {
    let ele = document.getElementsByClassName("recharts-yAxis")[0];
    ele.style = "transform: translateX(" + e.target.scrollLeft + "px);";
  };

  const getMetricDisplayName = (metric) => {
    if (metric.endsWith("_cumulative")) {
      return `${metric.replace("_cumulative", "")} (Cumulative)`;
    }
    return metric;
  };

  return (
    <div className="h-[600px] w-full">
      <style>
        {`
          .recharts-cartesian-axis.recharts-yAxis {
            transform: translate(0, 0);
            transition: transform 0s;
          }
        `}
      </style>
      <div
        className="h-full overflow-x-auto overflow-y-hidden"
        onScroll={handleScroll}
      >
        <div
          style={{
            minWidth: Math.max(data.length * 30, 1200) + "px",
            height: "600px",
          }}
        >
          <ResponsiveContainer width="100%" height={600}>
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, bottom: 60, left: 20 }}
              style={{ paddingLeft: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              {metrics.map((metric, index) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stroke={colors[index]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                  name={getMetricDisplayName(metric)}
                  strokeDasharray={
                    lineStyles[index] === "dashed" ? "5 5" : undefined
                  }
                />
              ))}
              <Brush dataKey="name" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const GlanceableLineChart = ({ data, metrics, colors, lineStyles = [] }) => {
  const getMetricDisplayName = (metric) => {
    if (metric.endsWith("_cumulative")) {
      return `${metric.replace("_cumulative", "")} (Cumulative)`;
    }
    return metric;
  };

  return (
    <div className="h-[600px] w-full">
      <ResponsiveContainer width="100%" height={600}>
        <LineChart
          data={data}
          margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
          <XAxis
            dataKey="name"
            interval={Math.ceil(data.length / 15)}
            tick={{ fontSize: 10 }}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          {metrics.map((metric, index) => (
            <Line
              key={metric}
              type="monotone"
              dataKey={metric}
              stroke={colors[index]}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 5 }}
              name={getMetricDisplayName(metric)}
              connectNulls={true}
              strokeDasharray={
                lineStyles[index] === "dashed" ? "5 5" : undefined
              }
            />
          ))}
          <Brush dataKey="name" height={30} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Combined component with toggle
const MultiMetricLineChart = ({ data, metrics, colors, lineStyles = [] }) => {
  const [viewMode, setViewMode] = useState("detailed"); // "detailed" or "glanceable"

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="bg-gray-100 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setViewMode("detailed")}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === "detailed"
                ? "bg-white shadow-sm text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Detailed
          </button>
          <button
            onClick={() => setViewMode("glanceable")}
            className={`px-3 py-1 text-sm rounded-md ${
              viewMode === "glanceable"
                ? "bg-white shadow-sm text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Overview
          </button>
        </div>
      </div>

      {viewMode === "detailed" ? (
        <DetailedLineChart
          data={data}
          metrics={metrics}
          colors={colors}
          lineStyles={lineStyles}
        />
      ) : (
        <GlanceableLineChart
          data={data}
          metrics={metrics}
          colors={colors}
          lineStyles={lineStyles}
        />
      )}
    </div>
  );
};

const DynamicDashboard = ({
  data,
  chartType = "trend",
  title = "Analytics Dashboard",
}) => {
  const [selectedChartType, setSelectedChartType] = useState("line");
  const [compareMetrics, setCompareMetrics] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState(null);
  const [showCumulative, setShowCumulative] = useState(false);
  const [timeframe, setTimeframe] = useState("day");

  // Check if data is valid
  const isDataValid = useMemo(() => {
    return Array.isArray(data) && data.length > 0 && data[0]?.metrics;
  }, [data]);

  // Extract metrics from the first item in the data array
  const metrics = useMemo(() => {
    if (!isDataValid) return [];
    return Object.entries(data[0].metrics).map(([key, value]) => ({
      value: key,
      label: value.display,
      unit: value.unit,
      type: value.type,
    }));
  }, [data, isDataValid]);

  // Set the first metric as the default selected metric
  const [selectedMetrics, setSelectedMetrics] = useState([]);

  // Set initial selected metric when metrics become available
  useEffect(() => {
    if (metrics.length > 0 && selectedMetrics.length === 0) {
      setSelectedMetrics([metrics[0].value]);
    }
  }, [metrics, selectedMetrics]);

  // Group metrics by type
  const metricsByType = useMemo(() => {
    return metrics.reduce((acc, metric) => {
      if (!acc[metric.type]) {
        acc[metric.type] = [];
      }
      acc[metric.type].push(metric);
      return acc;
    }, {});
  }, [metrics]);

  // Get the type of the first selected metric
  const selectedType = useMemo(() => {
    if (selectedMetrics.length === 0) return null;
    const firstMetric = metrics.find((m) => m.value === selectedMetrics[0]);
    return firstMetric?.type;
  }, [selectedMetrics, metrics]);

  // Get week number for a date
  const processedTrendData = useMemo(() => {
    if (!isDataValid || !data[0]?.data || chartType !== "trend") return [];

    const rawData = data[0].data;
    const tempData = new Map();
    const cumulativeValues = {};

    // Initialize cumulative values for each metric
    selectedMetrics.forEach((metric) => {
      cumulativeValues[metric] = 0;
    });

    // Get all dates and sort them
    const sortedDates = Object.keys(rawData)
      .filter((date) => date !== "Daily")
      .sort((a, b) => new Date(a) - new Date(b));

    if (sortedDates.length === 0) return [];

    if (timeframe === "week" && sortedDates.length > 0) {
      // For weekly view, group by 7-day periods
      const startDate = new Date(sortedDates[0]);
      let currentWeekStart = startDate;

      sortedDates.forEach((date) => {
        const currentDate = new Date(date);
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);

        if (currentDate >= weekEnd) {
          currentWeekStart = weekEnd;
        }

        const timeframeKey = `${currentWeekStart.getDate()} ${currentWeekStart.toLocaleString(
          "default",
          { month: "short" }
        )}, ${String(currentWeekStart.getFullYear()).slice(2)}`;

        if (!tempData.has(timeframeKey)) {
          tempData.set(timeframeKey, {
            name: timeframeKey,
            ...selectedMetrics.reduce(
              (acc, metric) => ({ ...acc, [metric]: 0 }),
              {}
            ),
          });
        }

        const timeframeData = tempData.get(timeframeKey);
        Object.entries(rawData[date] || {}).forEach(
          ([platform, platformData]) => {
            if (platform !== "glid" && platformData) {
              selectedMetrics.forEach((metric) => {
                timeframeData[metric] += platformData[metric] || 0;
              });
            }
          }
        );
      });
    } else {
      // Daily view - process as before
      sortedDates.forEach((date) => {
        const dateObj = new Date(date);
        const timeframeKey = `${dateObj.getDate()} ${dateObj.toLocaleString(
          "default",
          { month: "short" }
        )}, ${String(dateObj.getFullYear()).slice(2)}`;

        tempData.set(timeframeKey, {
          name: timeframeKey,
          ...selectedMetrics.reduce(
            (acc, metric) => ({ ...acc, [metric]: 0 }),
            {}
          ),
        });

        const timeframeData = tempData.get(timeframeKey);
        Object.entries(rawData[date] || {}).forEach(
          ([platform, platformData]) => {
            if (platform !== "glid" && platformData) {
              selectedMetrics.forEach((metric) => {
                timeframeData[metric] += platformData[metric] || 0;
              });
            }
          }
        );
      });
    }

    const transformedData = Array.from(tempData.values());

    // Add cumulative values if enabled
    if (showCumulative) {
      transformedData.forEach((data) => {
        selectedMetrics.forEach((metric) => {
          cumulativeValues[metric] += data[metric];
          data[`${metric}_cumulative`] = cumulativeValues[metric];
        });
      });
    }

    return transformedData;
  }, [
    data,
    selectedMetrics,
    chartType,
    showCumulative,
    timeframe,
    isDataValid,
  ]);

  const colors = useMemo(() => {
    const baseHues = [210, 330, 150, 270, 30, 180, 0, 240, 60, 300];
    return selectedMetrics.map((_, index) => {
      const hue = baseHues[index % baseHues.length];
      return `hsl(${hue}, 65%, 55%)`;
    });
  }, [selectedMetrics]);

  const toggleMetric = (metric) => {
    // debugger;
    const metricData = metrics.find((m) => m.value === metric);

    if (!compareMetrics && metric) {
      setSelectedMetrics([metric]);
      // toast.success("If you want to see these metrics together, turn on `Compare Metrics` option.", {
      //   icon: <Info className="w-10 h-10 text-yellow-600"/>,
      //   duration: 6000
      // })
      return;
    }

    if (selectedMetrics.includes(metric)) {
      setSelectedMetrics((prev) => prev.filter((m) => m !== metric));
    } else {
      if (
        selectedMetrics.length === 0 ||
        (compareMetrics && metricData.type === selectedType)
      ) {
        setSelectedMetrics((prev) => [...prev, metric]);
      }
    }
  };

  const handleCompareChange = (newValue) => {
    setCompareMetrics(newValue);
    if (!newValue && selectedMetrics.length > 1) {
      setSelectedMetrics([selectedMetrics[0]]);
    }
  };

  // Initialize breakdown selection if needed
  React.useEffect(() => {
    if (
      chartType === "breakdown" &&
      !selectedBreakdown &&
      isDataValid &&
      data[0]?.data
    ) {
      const firstBreakdown = Object.keys(data[0].data).find(
        (key) => key !== "Daily"
      );
      setSelectedBreakdown(firstBreakdown);
    }
  }, [chartType, data, selectedBreakdown, isDataValid]);

  // If data is null, undefined, or empty array, render an empty state message
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <FullscreenWrapper>
        <Card className="h-full flex flex-col">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-2">No data available</h3>
              <p className="text-muted-foreground">
                There is no data to display for the given date(s).
              </p>
            </div>
          </CardContent>
        </Card>
      </FullscreenWrapper>
    );
  }

  // If data exists but doesn't have the expected structure
  if (!isDataValid) {
    return (
      <FullscreenWrapper>
        <Card className="h-full flex flex-col">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <Alert className="w-full max-w-lg">
              <AlertDescription>
                The data structure is invalid or missing required properties.
                Please check the data format.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </FullscreenWrapper>
    );
  }

  return (
    <FullscreenWrapper>
      <div className="flex flex-col gap-4 h-full">
        <Card>
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Empty metrics state */}
            {metrics.length === 0 ? (
              <Alert>
                <AlertDescription>
                  No metrics found in the data.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="space-y-4">
                  {Object.entries(metricsByType).map(([type, typeMetrics]) => (
                    <div key={type} className="space-y-2">
                      <Label className="capitalize">{type} Metrics</Label>
                      <div className="flex flex-wrap gap-2">
                        {typeMetrics.map((metric) => {
                          const isSelected = selectedMetrics.includes(
                            metric.value
                          );
                          const canSelect =
                            !compareMetrics ||
                            !selectedType ||
                            metric.type === selectedType;

                          return (
                            <Badge
                              key={metric.value}
                              variant={isSelected ? "default" : "outline"}
                              className={`cursor-pointer ${
                                !canSelect && !isSelected ? "opacity-50" : ""
                              }`}
                              onClick={() =>
                                canSelect && toggleMetric(metric.value)
                              }
                            >
                              {metric.label}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="compare-metrics"
                      checked={compareMetrics}
                      onCheckedChange={handleCompareChange}
                    />
                    <Label htmlFor="compare-metrics">Compare Metrics</Label>
                  </div>

                  {chartType === "trend" && (
                    <>
                      {/* <div className="flex items-center space-x-2">
                        <Switch
                          id="show-cumulative"
                          checked={showCumulative}
                          onCheckedChange={setShowCumulative}
                        />
                        <Label htmlFor="show-cumulative">Show Cumulative</Label>
                      </div> */}

                      <div className="flex items-center gap-2 relative">
                        <Label>Group By:</Label>
                        <Select value={timeframe} onValueChange={setTimeframe}>
                          <SelectTrigger className="w-[100px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="day">Day</SelectItem>
                            <SelectItem value="week">Week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>

                {compareMetrics && selectedType && (
                  <Alert>
                    <AlertDescription>
                      You can only compare metrics of type:{" "}
                      <span className="font-medium capitalize">
                        {selectedType}
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Card className="flex-1">
          <CardHeader>
            <CardTitle>
              {compareMetrics
                ? `${selectedType} Metrics Comparison`
                : `${
                    metrics.find((m) => m.value === selectedMetrics[0])
                      ?.label || "No Metric Selected"
                  }`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Empty data state for the chart */}
            {(chartType === "breakdown" &&
              (!data[0]?.data || !data[0]?.data[selectedBreakdown])) ||
            (chartType === "trend" && processedTrendData.length === 0) ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">
                  No data available for the selected options
                </p>
              </div>
            ) : (
              <>
                {compareMetrics && (
                  <MetricComparisonCards
                    data={
                      chartType === "breakdown"
                        ? data[0].data[selectedBreakdown] || {}
                        : processedTrendData
                    }
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    colors={colors}
                    chartType={chartType}
                  />
                )}
                {chartType === "breakdown" ? (
                  <div>
                    <BreakdownChart
                      data={data}
                      selectedMetrics={selectedMetrics}
                      selectedBreakdown={selectedBreakdown}
                      onBreakdownChange={setSelectedBreakdown}
                      colors={colors}
                    />

                    <div className="flex justify-end">
                      <ExportButton
                        className="ml-auto"
                        onExport={() =>
                          exportToExcel({
                            data,
                            metrics,
                            selectedMetrics,
                            showCumulative,
                            timeframe,
                            chartType,
                            selectedBreakdown,
                            title,
                          })
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {compareMetrics && (
                      <Tabs
                        value={selectedChartType}
                        onValueChange={setSelectedChartType}
                      >
                        <TabsList>
                          <TabsTrigger
                            value="line"
                            className="flex gap-2 items-center"
                          >
                            <LineIcon className="h-4 w-4" />
                            Line Chart
                          </TabsTrigger>
                          <TabsTrigger
                            value="bar"
                            className="flex gap-2 items-center"
                          >
                            <BarIcon className="h-4 w-4" />
                            Bar Chart
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="line">
                          <MultiMetricLineChart
                            data={processedTrendData}
                            metrics={selectedMetrics.flatMap((metric) =>
                              showCumulative
                                ? [metric, `${metric}_cumulative`]
                                : [metric]
                            )}
                            colors={colors.flatMap((color) =>
                              showCumulative ? [color, color] : [color]
                            )}
                            lineStyles={colors.flatMap((color) =>
                              showCumulative ? ["solid", "dashed"] : ["solid"]
                            )}
                          />
                        </TabsContent>
                        <TabsContent value="bar">
                          <MultiMetricBarChart
                            data={processedTrendData}
                            metrics={selectedMetrics.flatMap((metric) =>
                              showCumulative
                                ? [metric, `${metric}_cumulative`]
                                : [metric]
                            )}
                            colors={colors.flatMap((color) =>
                              showCumulative ? [color, color] : [color]
                            )}
                          />
                        </TabsContent>
                      </Tabs>
                    )}
                    {!compareMetrics && (
                      <MultiMetricLineChart
                        data={processedTrendData}
                        metrics={selectedMetrics.flatMap((metric) =>
                          showCumulative
                            ? [metric, `${metric}_cumulative`]
                            : [metric]
                        )}
                        colors={colors.flatMap((color) =>
                          showCumulative ? [color, color] : [color]
                        )}
                        lineStyles={colors.flatMap(() =>
                          showCumulative ? ["solid", "dashed"] : ["solid"]
                        )}
                      />
                      // <GlanceableLineChart
                      //   data={processedTrendData}
                      //   metrics={selectedMetrics.flatMap((metric) =>
                      //     showCumulative
                      //       ? [metric, `${metric}_cumulative`]
                      //       : [metric]
                      //   )}
                      //   colors={colors.flatMap((color) =>
                      //     showCumulative ? [color, color] : [color]
                      //   )}
                      //   lineStyles={colors.flatMap(() =>
                      //     showCumulative ? ["solid", "dashed"] : ["solid"]
                      //   )}
                      // />
                    )}
                    <div className="flex justify-end">
                      <ExportButton
                        className="ml-auto"
                        onExport={() =>
                          exportToExcel({
                            data,
                            metrics,
                            selectedMetrics,
                            showCumulative,
                            timeframe,
                            chartType,
                            selectedBreakdown,
                            title,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </FullscreenWrapper>
  );
};

export default DynamicDashboard;
